<template>
  <div class="online_order_board">
    <div
      v-for="col in columnConfigs"
      :key="col.key"
      class="online_order_board__column"
    >
      <div class="online_order_board__column_title">
        {{ col.title }}
      </div>
      <div
        :ref="`scroll_${col.key}`"
        class="online_order_board__column_body"
        @scroll="onColumnScroll(col.key, $event)"
      >
        <OnlineOrderCard
          v-for="order in columns[col.key].list"
          :key="order.id"
          :order="order"
          :status="col.key"
          @accept="handleAccept"
          @reject="handleReject"
          @notify-pickup="handleNotifyPickup"
          @reprint="handleReprint"
          @cancel="handleCancel"
          @complete-pickup="handleCompletePickup"
          @notify-pickup-again="handleNotifyPickupAgain"
        />
        <div v-if="columns[col.key].loading" class="online_order_board__loading">
          <i class="el-icon-loading"></i>
        </div>
        <div
          v-else-if="columns[col.key].finished && !columns[col.key].list.length"
          class="online_order_board__empty"
        >
          {{ $t('OrderFoodManagement.OnlineOrderEmpty') }}
        </div>
      </div>
    </div>
    <!-- 拒絕接單備註 Dialog -->
    <el-dialog
      :visible.sync="rejectDialog"
      :close-on-click-modal="false"
      :lock-scroll="true"
      center
      custom-class="reject-dialog"
      :show-close="false"
    >
      <template #title>
        <div class="reject-dialog__header">
          <span class="reject-dialog__title el-dialog__title">{{ $t('OrderFoodManagement.OnlineOrderRejectDialogTitle') }}</span>
          <el-tooltip :content="$t('OrderFoodManagement.OnlineOrderRejectDialogTooltip')" placement="top">
            <span><i class="icon-questions-circular-button"></i></span>
          </el-tooltip>
        </div>
      </template>
      <el-form ref="rejectFormRef" :model="rejectForm" :rules="rejectFormRules" class="reject-dialog__form">
        <el-form-item>
          <el-select v-model="rejectSelectedNote" @change="onRejectNoteSelectChange">
            <el-option
              v-for="item in rejectNoteTemplates"
              :key="item.Id"
              :label="item.Note"
              :value="item.Note"
            />
          </el-select>
        </el-form-item>
        <el-form-item prop="noteInputValue">
          <el-input
            v-model="rejectForm.noteInputValue"
            type="textarea"
            rows="4"
            maxlength="100"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button round @click="closeRejectDialog">{{ $t('General.Cancel') }}</el-button>
        <el-button type="primary" round @click="confirmReject">{{ $t('OrderFoodManagement.OnlineOrderRejectSubmitButton') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import OnlineOrderCard from './OnlineOrderCard.vue'
import { apiOrderFood } from '@/api/OrderFood'
import { apiOrderFood as apiOrderFoodV1 } from '@/api/point/orderFood'
import { convertKeysToCamelCase, convertKeysToPascalCase } from '@/js/utils/bff'

dayjs.extend(utc)

/** 每次滾軸加載的筆數 */
const PAGE_SIZE = 5
/** 滾軸距離底部多少 px 時觸發加載 */
const SCROLL_THRESHOLD = 100

export default {
  name: 'OnlineOrderBoard',
  components: {
    OnlineOrderCard
  },
  props: {
    /** 當前頁籤是否為啟用狀態，用於控制是否拉取資料 */
    isActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      /**
       * 三欄看板資料，各欄獨立管理
       * - list: 已載入的訂單陣列
       * - offset: 目前已載入筆數（API 分頁位移）
       * - totalCount: 該欄後端總筆數
       * - loading: 是否正在載入
       * - finished: 是否已載完全部資料
       */
      columns: {
        pending: { list: [], offset: 0, totalCount: 0, loading: false, finished: false },
        preparing: { list: [], offset: 0, totalCount: 0, loading: false, finished: false },
        pickup: { list: [], offset: 0, totalCount: 0, loading: false, finished: false }
      },
      // 拒單 dialog
      rejectDialog: false,
      rejectOrder: null,
      rejectForm: {
        noteInputValue: ''
      },
      rejectFormRules: {
        noteInputValue: [
          {
            type: 'string',
            required: true,
            message: this.$t('OrderFoodManagement.OnlineOrderRejectNoteRequired'),
            trigger: ['change', 'blur']
          },
          {
            validator: (rule, value, callback) => {
              if (!value || value.trim() === '') {
                callback(new Error(this.$t('OrderFoodManagement.OnlineOrderRejectNoteRequired')))
              } else {
                callback()
              }
            },
            trigger: ['change', 'blur']
          }
        ]
      },
      rejectNoteTemplates: [],
      rejectSelectedNote: null
    }
  },
  computed: {
    /** 三欄配置（key 對應 columns 的 key，title 為欄標題） */
    columnConfigs () {
      return [
        { key: 'pending', title: this.$t('OrderFoodManagement.OnlineOrderColumnPending') },
        { key: 'preparing', title: this.$t('OrderFoodManagement.OnlineOrderColumnPreparing') },
        { key: 'pickup', title: this.$t('OrderFoodManagement.OnlineOrderColumnPickup') }
      ]
    },
    /** API 查詢時間範圍：過去 7 天 ~ 未來 3 天 */
    dateRange () {
      const fmt = 'YYYY-MM-DDTHH:mm:ssZ'
      return {
        StartDateTime: dayjs().utcOffset(8).subtract(7, 'day').startOf('day').format(fmt),
        EndDateTime: dayjs().utcOffset(8).add(3, 'day').startOf('day').add(1, 'day').format(fmt)
      }
    }
  },
  watch: {
    // 頁籤由非啟用切換為啟用時重新拉取資料
    isActive (val, oldVal) {
      if (val && !oldVal) {
        this.refreshAll()
      }
    }
  },
  mounted () {
    if (this.isActive) {
      this.refreshAll()
    }
    this.getRejectNoteTemplates()
  },
  methods: {
    /** 重新拉取所有欄位資料（操作完成或頁籤切回時呼叫） */
    refreshAll () {
      this.fetchColumn('pending', { reset: true })
      this.fetchColumn('preparing', { reset: true })
      this.fetchColumn('pickup', { reset: true })
    },
    /**
     * 拉取指定欄位的訂單資料
     * @param {string} key 欄位 key（pending / preparing / pickup）
     * @param {Object} options
     * @param {boolean} options.reset 是否清空既有資料並從頭載入
     */
    async fetchColumn (key, { reset = false } = {}) {
      const col = this.columns[key]
      if (col.loading) return
      if (!reset && col.finished) return

      if (reset) {
        col.list = []
        col.offset = 0
        col.totalCount = 0
        col.finished = false
      }

      col.loading = true

      try {
        // 各欄對應的 API
        const apiMap = {
          pending: apiOrderFood.getTableFoodFormOrderPending,
          preparing: apiOrderFood.getTableFoodFormMealPending,
          pickup: apiOrderFood.getTableFoodFormPickupPending
        }
        const apiFn = apiMap[key]
        if (!apiFn) {
          col.finished = true
          return
        }

        const params = convertKeysToPascalCase({
          ...this.dateRange,
          offset: col.offset,
          maxCount: PAGE_SIZE
        })
        const { status, data } = convertKeysToCamelCase((await apiFn(params)).data)

        if (status === 1) {
          const forms = data.forms || []
          col.list.push(...forms)
          col.totalCount = data.totalCount || 0
          col.offset += forms.length
          // 已載入筆數 >= 總筆數 或 本次回傳為空 → 標記為載完
          if (col.list.length >= col.totalCount || forms.length === 0) {
            col.finished = true
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      } finally {
        col.loading = false
      }
    },
    /**
     * 欄位滾軸事件：接近底部時自動加載下一頁
     * @param {string} key 欄位 key
     * @param {Event} event 原生 scroll 事件
     */
    onColumnScroll (key, event) {
      const el = event.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD) {
        this.fetchColumn(key)
      }
    },
    /**
     * 接單：呼叫接單 API，成功後 emit 到上層進行列印明細單
     * @param {Object} order 訂單資料
     */
    async handleAccept (order) {
      const loading = this.$loading({ lock: true, text: '處理中，請稍候', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.7)' })
      try {
        const { status } = convertKeysToCamelCase((await apiOrderFood.acceptTableFoodFormOrder(order.id, { Note: '' })).data)
        if (status === 1) {
          this.refreshAll()
          // emit 到上層進行列印明細單，由上層負責關閉 loading
          this.$emit('accept-print', { order, loading })
        } else {
          loading.close()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
        const code = error?.response?.data?.ErrorMessage?.Code
        if (code === 400000) {
          this.refreshAll()
        }
        loading.close()
      }
    },
    /**
     * 拒單：開啟拒絕接單備註 dialog
     * @param {Object} order 訂單資料
     */
    handleReject (order) {
      this.rejectOrder = order
      this.rejectDialog = true
    },
    /** 關閉拒單 dialog 並重置表單 */
    closeRejectDialog () {
      this.rejectSelectedNote = null
      this.rejectForm.noteInputValue = ''
      this.rejectDialog = false
      this.rejectOrder = null
    },
    /** 拒單備註範本選取後帶入輸入框 */
    onRejectNoteSelectChange (value) {
      this.rejectForm.noteInputValue = value
    },
    /** 取得拒單備註範本 */
    async getRejectNoteTemplates () {
      try {
        const res = await apiOrderFoodV1.getRejectNoteTemplates()
        this.rejectNoteTemplates = res.data.Data.NoteTemplates || []
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      }
    },
    /** 確認拒單：驗證表單後呼叫 API */
    confirmReject () {
      this.$refs.rejectFormRef.validate(async (valid) => {
        if (!valid) return
        const loading = this.$loading({ lock: true, text: '處理中，請稍候', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.7)' })
        try {
          const payload = convertKeysToPascalCase({ note: this.rejectForm.noteInputValue })
          const { status } = convertKeysToCamelCase((await apiOrderFood.cancelTableFoodFormOrder(this.rejectOrder.id, payload)).data)
          if (status === 1) {
            this.$message.success(this.$t('OrderFoodManagement.OnlineOrderRejectSuccessToast'))
            const order = this.rejectOrder
            // 拒單原因需在關閉 dialog 前取出（關閉時會清空輸入值）
            const note = this.rejectForm.noteInputValue
            this.closeRejectDialog()
            this.refreshAll()
            // emit 到上層列印「作廢/取消」明細單，由上層負責關閉 loading
            this.$emit('void-cancel-print', { order, loading, note })
          } else {
            loading.close()
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error)
          loading.close()
        }
      })
    },
    /**
     * 通知取餐
     * @param {Object} order 訂單資料
     */
    handleNotifyPickup (order) {
      const loading = this.$loading({ lock: true, text: '處理中，請稍候', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.7)' })
      apiOrderFood.notifyTableFoodFormMealReady(order.id, { Note: '' })
        .then(res => {
          const { status } = convertKeysToCamelCase(res.data)
          if (status === 1) {
            this.$message.success(this.$t('OrderFoodManagement.OnlineOrderNotifyPickupSuccessChangeStatus'))
            this.$nextTick(() => {
              this.$message.success(this.$t('OrderFoodManagement.OnlineOrderNotifyPickupSuccess'))
            })
            this.refreshAll()
          }
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log(error)
        })
        .finally(() => {
          loading.close()
        })
    },
    /**
     * 準備中取消：emit 到上層，共用作廢 dialog 流程，最終呼叫準備中作廢 API
     * @param {Object} order 訂單資料
     */
    handleCancel (order) {
      this.$emit('void-order', {
        id: order.id,
        actualAmount: order.actualAmount,
        tabletNote: order.tabletNote,
        itemsPlain: this.formatItemsPlain(order),
        paymentMode: order.paymentMode,
        normalActualTotalAmount: order.normalActualTotalAmount,
        cashActualTotalAmount: order.cashActualTotalAmount,
        voidApiType: 'cancelMeal'
      })
    },
    /**
     * 補單：emit 到上層，由 OrderFoodManagement 重用既有補單流程
     * @param {Object} order 訂單資料
     */
    handleReprint (order) {
      this.$emit('reprint-order', order)
    },
    /**
     * 完成取餐：先跳確認對話框，確認後呼叫 API
     * @param {Object} order 訂單資料
     */
    handleCompletePickup (order) {
      this.$confirm(
        this.$t('OrderFoodManagement.OnlineOrderCompletePickupConfirm'),
        this.$t('General.AlertTitle'),
        {
          confirmButtonText: this.$t('General.Confirm'),
          cancelButtonText: this.$t('General.Cancel'),
          type: 'warning'
        }
      ).then(async () => {
        const loading = this.$loading({ lock: true, text: '處理中，請稍候', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.7)' })
        try {
          const payload = convertKeysToPascalCase({
            ids: [order.id],
            pickupType: 1,
            pickupId: '',
            note: ''
          })
          const { status } = convertKeysToCamelCase((await apiOrderFood.completeTableFoodFormPickup(payload)).data)
          if (status === 1) {
            this.$message.success(this.$t('OrderFoodManagement.OnlineOrderCompletePickupSuccess'))
            this.refreshAll()
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error)
        } finally {
          loading.close()
        }
      }).catch(() => {})
    },
    /**
     * 待取餐通知取餐：先跳確認對話框，確認後呼叫 API
     * @param {Object} order 訂單資料
     */
    handleNotifyPickupAgain (order) {
      this.$confirm(
        this.$t('OrderFoodManagement.OnlineOrderNotifyPickupAgainConfirm'),
        this.$t('General.AlertTitle'),
        {
          confirmButtonText: this.$t('General.Confirm'),
          cancelButtonText: this.$t('General.Cancel'),
          type: 'warning'
        }
      ).then(async () => {
        const loading = this.$loading({ lock: true, text: '處理中，請稍候', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.7)' })
        try {
          const { status } = convertKeysToCamelCase((await apiOrderFood.notifyTableFoodFormPickup(order.id)).data)
          if (status === 1) {
            this.$message.success(this.$t('OrderFoodManagement.OnlineOrderNotifyPickupAgainSuccess'))
            this.refreshAll()
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error)
        } finally {
          loading.close()
        }
      }).catch(() => {})
    },
    /**
     * 將訂單品項格式化為純文字（作廢 dialog 顯示用）
     * @param {Object} order 訂單資料
     * @returns {string} 品項文字，以換行分隔
     */
    formatItemsPlain (order) {
      if (!order.items || !order.items.length) return ''
      return order.items.map(item => {
        const groupLabel = item.groupName ? `【${item.groupName}】` : ''
        const choosePart = item.chooseName ? `（${item.chooseName}）` : ''
        return `${groupLabel}${item.itemName}${choosePart}：${item.quantity}`
      }).join('\n')
    }
  }
}
</script>

<style lang="scss" scoped>
.online_order_board {
  display: flex;
  gap: 16px;
  height: calc(100vh - 260px);
  min-height: 400px;

  &__column {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 8px;
    border: solid 1px #ccc;
    overflow: hidden;
  }

  &__column_title {
    font-size: map-get($fontSize, xx-large);
    font-weight: 600;
    margin-bottom: 20px;
    padding: 20px 20px 0 20px;
  }

  &__column_body {
    width: 100%;
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0 16px 16px 16px;
    box-sizing: border-box;
  }

  &__loading {
    text-align: center;
    padding: 16px;
    color: #999;
    font-size: map-get($fontSize, large);
  }

  &__empty {
    text-align: center;
    padding: 32px 16px;
    color: #999;
    font-size: map-get($fontSize, small);
  }
}
::v-deep .reject-dialog {
  @include basicDialog();
  &__header{
    display: flex;
    align-items: center;
  }
  &__title{
    margin-right:.5rem
  }
  i{
    font-size: map-get($fontSize,large);
    color:$mainColor
  }
  &__form{
    .el-form-item,.el-select{
    width: 100%;
    }
  }
}
</style>
